import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Tabs,
  Tab,
  Alert,
} from "react-bootstrap";
import { FaUpload, FaCheck, FaTimes } from "react-icons/fa";
import banksLogo from "../../Resources/banks_logo.jpg";
import ozowLogo from "../../Resources/ozow_logo.png";
import yocoLogo from "../../Resources/yoco_logo.png";
import { updateBankingDetails, decryptField } from "../../afinanceapi";
import YocoConnect from "./YocoConnect";

export default function EditBankingModal({
  show,
  onHide,
  onBankingUpdate = () => {},
  bankingDetail,
  practiceName = "",
}) {
  const [successAlert, setSuccessAlert] = useState(false);
  const [closeAlert, setCloseAlert] = useState(false);
  const [bank, setBank] = useState({});
  const [ozow, setOzow] = useState({});
  const [yoco, setYoco] = useState({});

  useEffect(() => {
    async function decryptFields() {
      const decryptIfNeeded = async (value) =>
        typeof value === "string" && value.includes(":")
          ? await decryptField(value)
          : value;

      setBank({
        bankName: bankingDetail.bank.bankName,
        branchCode: bankingDetail.bank.branchCode,
        accountNumber: await decryptIfNeeded(bankingDetail.bank.accountNumber),
        accountName: await decryptIfNeeded(bankingDetail.bank.accountName),
        accountType: bankingDetail.bank.accountType,
      });

      setOzow({
        merchantCode: bankingDetail.ozow.merchantCode,
        apiKey: await decryptIfNeeded(bankingDetail.ozow.apiKey),
        privateKey: await decryptIfNeeded(bankingDetail.ozow.privateKey),
        paymentLink: bankingDetail.ozow.paymentLink,
        onlinePayment: bankingDetail.ozow.onlinePayment,
      });

      setYoco({
        merchantId: bankingDetail.yoco.merchantId,
        apiKey: await decryptIfNeeded(bankingDetail.yoco.apiKey),
        privateKey: await decryptIfNeeded(bankingDetail.yoco.privateKey),
        cardMachine: bankingDetail.yoco.cardMachine,
        paymentLink: bankingDetail.yoco.paymentLink,
        onlinePayment: bankingDetail.yoco.onlinePayment,
      });
    }

    if (bankingDetail) {
      decryptFields();
    }
  }, [bankingDetail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        practiceNumber: bankingDetail.practiceNumber,
        bank,
        ozow,
        yoco,
      };
      await updateBankingDetails(bankingDetail._id, payload);
      setSuccessAlert(true);
      setTimeout(() => {
        setSuccessAlert(false);
        onHide();
        onBankingUpdate();
      }, 1000);
    } catch (error) {
      console.error("Error updating banking details:", error);
    }
  };

  const handleClose = () => setCloseAlert(true);
  const handleCancelNo = () => setCloseAlert(false);
  const handleCancelYes = () => {
    setCloseAlert(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton={!closeAlert && !successAlert}>
        <Modal.Title className="text-primary">
          Edit Banking & Payment Details
        </Modal.Title>
      </Modal.Header>

      {closeAlert && (
        <Alert variant="info" className="d-flex justify-content-between">
          <span>
            Are you sure you want to close? Changes will not be saved.
          </span>
          <div>
            <Button variant="outline-primary" onClick={handleCancelYes}>
              <FaCheck />
            </Button>
            <Button variant="outline-secondary" onClick={handleCancelNo}>
              <FaTimes />
            </Button>
          </div>
        </Alert>
      )}

      {successAlert && (
        <Alert variant="success">
          The banking details were updated successfully!
        </Alert>
      )}

      <Modal.Body>
        <h5 className="text-muted mb-3">Practice: {practiceName}</h5>
        <Form onSubmit={handleSubmit}>
          <Tabs defaultActiveKey="banking" className="mb-3" justify>
            <Tab
              eventKey="banking"
              title={<img src={banksLogo} alt="banks" style={{ height: 30 }} />}
            >
              <Row className="mb-3">
                <Form.Group as={Col}>
                  <Form.Label>Bank Name</Form.Label>
                  <Form.Control
                    value={bank.bankName || ""}
                    onChange={(e) =>
                      setBank({ ...bank, bankName: e.target.value })
                    }
                  />
                </Form.Group>
                <Form.Group as={Col}>
                  <Form.Label>Branch Code</Form.Label>
                  <Form.Control
                    value={bank.branchCode || ""}
                    onChange={(e) =>
                      setBank({ ...bank, branchCode: e.target.value })
                    }
                  />
                </Form.Group>
              </Row>
              <Row>
                <Form.Group as={Col}>
                  <Form.Label>Account Number</Form.Label>
                  <Form.Control
                    value={bank.accountNumber || ""}
                    onChange={(e) =>
                      setBank({ ...bank, accountNumber: e.target.value })
                    }
                  />
                </Form.Group>
                <Form.Group as={Col}>
                  <Form.Label>Account Name</Form.Label>
                  <Form.Control
                    value={bank.accountName || ""}
                    onChange={(e) =>
                      setBank({ ...bank, accountName: e.target.value })
                    }
                  />
                </Form.Group>
                <Form.Group as={Col}>
                  <Form.Label>Account Type</Form.Label>
                  <Form.Control
                    value={bank.accountType || ""}
                    onChange={(e) =>
                      setBank({ ...bank, accountType: e.target.value })
                    }
                  />
                </Form.Group>
              </Row>
            </Tab>

            <Tab
              eventKey="ozow"
              title={<img src={ozowLogo} alt="ozow" style={{ height: 30 }} />}
            >
              <Row className="mb-3">
                <Form.Group as={Col}>
                  <Form.Label>Merchant Code</Form.Label>
                  <Form.Control
                    value={ozow.merchantCode || ""}
                    onChange={(e) =>
                      setOzow({ ...ozow, merchantCode: e.target.value })
                    }
                  />
                </Form.Group>
                <Form.Group as={Col}>
                  <Form.Label>API Key</Form.Label>
                  <Form.Control
                    value={ozow.apiKey || ""}
                    onChange={(e) =>
                      setOzow({ ...ozow, apiKey: e.target.value })
                    }
                  />
                </Form.Group>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Private Key</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={ozow.privateKey || ""}
                  onChange={(e) =>
                    setOzow({ ...ozow, privateKey: e.target.value })
                  }
                />
              </Form.Group>
              <Row>
                <Form.Check
                  className="ms-3"
                  type="checkbox"
                  label="Payment Links"
                  checked={ozow.paymentLink === "true"}
                  onChange={(e) =>
                    setOzow({
                      ...ozow,
                      paymentLink: e.target.checked ? "true" : "",
                    })
                  }
                />
                <Form.Check
                  className="ms-3"
                  type="checkbox"
                  label="Online Payments"
                  checked={ozow.onlinePayment === "true"}
                  onChange={(e) =>
                    setOzow({
                      ...ozow,
                      onlinePayment: e.target.checked ? "true" : "",
                    })
                  }
                />
              </Row>
            </Tab>

            <Tab
              eventKey="yoco"
              title={<img src={yocoLogo} alt="yoco" style={{ height: 30 }} />}
            >
              <YocoConnect practiceNumber={bankingDetail?.practiceNumber} />
            </Tab>
          </Tabs>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleSubmit}>
          <FaUpload className="me-2" /> Update
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
